import { CANONICAL, GIFI } from "../../../../../shared/helpers/constants";

// EQ_01..EQ_05, EQ_04, BS_01, RE_01, DIV_01 — share capital, retained earnings,
// dividends, and the overall balance-sheet balance check.

// Liability semantic classes that BS_01 considers when looking for liability
// accounts carrying debit balances (a sign of missing opening balances or
// misclassification). Used in three resolution paths inside resolveBs01.
const BS_LIABILITY_CLASSES = ["trade_payable", "debt", "current_portion_ltd", "deferred_revenue", "tax_payable"];

// ─── Custom resolvers ────────────────────────────────────────────────────────

// EQ_01 share_capital — phase 1: searches CoA for candidate share-capital
// accounts and asks the reviewer to confirm (or pick a different account).
function resolveSearchShareCapital(resolved, ctx) {
	let userResponses = ctx.userResponses;
	let chartOfAccounts = ctx.chartOfAccounts;

	// Phase 1a: status blocked, no response yet, candidates found → show them
	if (resolved.status === "blocked") {
		let keywords = ["share", "capital", "common", "class a", "class b", "authorized"];
		let candidates = (chartOfAccounts || []).filter(function (acct) {
			let desc = (acct.description || acct.accountDescription || "").toLowerCase();
			return keywords.some(function (kw) { return desc.includes(kw); });
		});
		if (candidates.length > 0 && !userResponses["search_share_capital"]) {
			let candidateOptions = candidates.map(function (acct) {
				let num = acct.accountNumber || acct.AC_NO || "";
				let desc = acct.description || acct.accountDescription || acct.AC_DESC || "";
				return { id: "link_" + num, label: desc + (num && !num.includes(".") ? " (" + num + ")" : "") };
			});
			candidateOptions.push({ id: "does_not_exist", label: "None of these — there is no share capital account" });
			return {
				...resolved,
				status: "blocked",
				detail: "Found " + candidates.length + " account(s) that may be share capital",
				prompt: {
					question: "We found these accounts in the chart of accounts. Is one of them the share capital account?",
					options: candidateOptions,
				},
			};
		}
	}

	// Phase 1b: response is in hand — interpret it
	if (userResponses["search_share_capital"]) {
		let response = userResponses["search_share_capital"];
		if (response === "show_all") {
			let allOptions = (chartOfAccounts || []).map(function (acct) {
				let num = acct.accountNumber || acct.AC_NO || "";
				let desc = acct.description || acct.accountDescription || acct.AC_DESC || "";
				return { id: "link_" + num, label: desc + (num && !num.includes(".") ? " (" + num + ")" : "") };
			});
			return { ...resolved, status: "blocked", detail: "Showing all accounts", prompt: { question: "Which account is the share capital?", options: allOptions } };
		}
		if (response.startsWith("link_")) {
			let linkedAccount = response.replace("link_", "");
			return { ...resolved, status: "completed", detail: "Account " + linkedAccount + " identified as share capital — needs mapping", prompt: null, data: { linkedAccount: linkedAccount } };
		}
		if (response === "exists_unmapped") {
			let keywords = ["share", "capital", "common", "class a", "class b", "authorized", "equity", "stock", "retained"];
			let candidates = (chartOfAccounts || []).filter(function (acct) {
				let desc = (acct.description || acct.accountDescription || "").toLowerCase();
				return keywords.some(function (kw) { return desc.includes(kw); });
			});
			if (candidates.length > 0) {
				let candidateOptions = candidates.map(function (acct) {
					let num = acct.accountNumber || acct.AC_NO || "";
					let desc = acct.description || acct.accountDescription || acct.AC_DESC || "";
					return { id: "link_" + num, label: desc + (num && !num.includes(".") ? " (" + num + ")" : "") };
				});
				candidateOptions.push({ id: "show_all", label: "None of these — show all accounts" });
				return {
					...resolved, status: "blocked",
					detail: "Select the share capital account from the chart of accounts",
					prompt: { question: "Which account is the share capital?", options: candidateOptions },
				};
			}
			let allAccounts = (chartOfAccounts || []).map(function (acct) {
				let num = acct.accountNumber || acct.AC_NO || "";
				let desc = acct.description || acct.accountDescription || acct.AC_DESC || "";
				return { id: "link_" + num, label: desc + (num && !num.includes(".") ? " (" + num + ")" : "") };
			});
			if (allAccounts.length > 0) {
				return { ...resolved, status: "blocked", detail: "No obvious share capital accounts found — select the correct account below", prompt: { question: "Which account is the share capital?", options: allAccounts } };
			}
			return { ...resolved, status: "completed", detail: "No accounts found in chart of accounts", prompt: null };
		}
		if (response === "does_not_exist") {
			return { ...resolved, status: "completed", detail: "Confirmed: no share capital account in the books", prompt: null };
		}
	}
	return resolved;
}

// EQ_01 share_capital — phase 2: confirm the picked account or prompt for an
// explicit share-capital amount if none exists.
function resolveResolveShareCapital(resolved, ctx) {
	let userResponses = ctx.userResponses;
	let step2Response = userResponses["search_share_capital"];
	if (!step2Response) return resolved;
	if (step2Response.startsWith("link_")) {
		let accountNum = step2Response.replace("link_", "");
		return { ...resolved, status: "completed", detail: "Account " + accountNum + " will be mapped to Share Capital", prompt: null };
	}
	if (step2Response === "exists_unmapped") {
		return { ...resolved, status: "blocked", detail: "Select the share capital account above first" };
	}
	if (step2Response === "does_not_exist") {
		let shareAmount = userResponses["resolve_share_capital_share_capital_amount"];
		if (shareAmount) {
			return { ...resolved, status: "completed", detail: "Share capital confirmed: $" + Number(shareAmount).toLocaleString("en-CA"), prompt: null };
		}
		return {
			...resolved,
			status: "waiting_for_human",
			detail: "No share capital account exists — enter the issued share capital amount below",
			prompt: {
				question: "What is the authorized and issued share capital for this corporation?",
				input_fields: [
					{ id: "share_capital_amount", label: "Share capital amount ($)", input_type: "number", default_value: 1 },
				],
			},
		};
	}
	return resolved;
}

// EQ_01 share_capital — phase 3: emit the mapping proposal OR the AJE that
// records share-capital issuance.
function resolvePostShareCapital(resolved, ctx) {
	let userResponses = ctx.userResponses;
	let chartOfAccounts = ctx.chartOfAccounts;
	let step2Response = userResponses["search_share_capital"];
	let shareAmount = userResponses["resolve_share_capital_share_capital_amount"];
	if (step2Response && step2Response.startsWith("link_")) {
		let accountNum = step2Response.replace("link_", "");
		let matchedAcct = (chartOfAccounts || []).find(function (a) { return (a.accountNumber || a.AC_NO || "") === accountNum; });
		let accountName = matchedAcct ? (matchedAcct.description || matchedAcct.accountDescription || matchedAcct.AC_DESC || accountNum) : accountNum;
		return {
			...resolved, status: "ready_to_propose", detail: null,
			inline_mapping: { accountNumber: accountNum, accountName: accountName, canonicalCode: CANONICAL.COMMON_SHARES, canonicalName: "Common shares" },
		};
	}
	if (step2Response === "exists_unmapped") {
		return { ...resolved, status: "blocked", detail: "Select the share capital account above first" };
	}
	if (step2Response === "does_not_exist" && shareAmount) {
		return {
			...resolved, status: "ready_to_propose", detail: null,
			inline_proposal: { amount: Number(shareAmount), debitCanonicalCode: CANONICAL.CASH, debitDescription: "Cash", debitGifi: GIFI.CASH, creditCanonicalCode: CANONICAL.COMMON_SHARES, creditDescription: "Common shares", creditGifi: GIFI.COMMON_SHARES, description: "Record share capital issuance" },
		};
	}
	return resolved;
}

// EQ_03 remap_to_partner — uses peer step's detail to flag share-capital remap.
function resolveEq03RemapToPartner(resolved, ctx) {
	let step1 = ctx.steps.find(function (s) { return s.id === "eq03_show_context"; });
	let shareEntries = step1 && step1.detail ? [{ accountNumber: "", accountName: "Share Capital", currentClass: "share_capital", canonicalCode: CANONICAL.OWNER_CAPITAL, canonicalName: "Partners' capital" }] : [];
	return { ...resolved, status: "ready_to_propose", detail: null, inline_mappings: shareEntries };
}

// EQ_04 remap_accounts — builds inline_mappings from the peer step's entries
// list, with target canonical depending on each entry's class.
function resolveEq04RemapAccounts(resolved, ctx) {
	let step2 = ctx.steps.find(function (s) { return s.id === "eq04_identify_problem_accounts"; });
	let entries = step2 && step2.data && step2.data.entries ? step2.data.entries : [];
	let mappings = entries.map(function (e) {
		let isDrawings = e.class === "drawings";
		return { accountNumber: e.code, accountName: e.name, currentClass: e.class, canonicalCode: isDrawings ? CANONICAL.DIVIDENDS_DECLARED : CANONICAL.COMMON_SHARES, canonicalName: isDrawings ? "Dividends Declared" : "Common shares" };
	});
	return { ...resolved, status: "ready_to_propose", detail: null, inline_mappings: mappings };
}

// BS_01 — balance-sheet difference: three resolution paths, all of which
// read the cross-check facts (effective entries) rather than peer steps.
function resolveBs01(resolved, ctx) {
	let userResponses = ctx.userResponses;
	let crossCheckFacts = ctx.crossCheckFacts;
	let response = userResponses["bs01_resolve"];
	if (!response) return resolved;

	if (response === "escalate") {
		return { ...resolved, status: "completed", detail: "Escalated to engagement partner for review", prompt: null };
	}

	let effectiveEntries = crossCheckFacts && crossCheckFacts.allEntries ? crossCheckFacts.allEntries : [];
	let debitLiabilities = effectiveEntries.filter(function (e) {
		return e.semantic && e.semantic.role && BS_LIABILITY_CLASSES.includes(e.semantic.role.class) && (e.net_balance || 0) < -1;
	});

	if (response === "add_opening_balances") {
		let allSubmitted = debitLiabilities.length > 0 && debitLiabilities.every(function (e) { return userResponses["ob_" + e.canonical_code]; });
		if (allSubmitted) {
			let proposals = debitLiabilities.map(function (e) {
				let obAmount = parseFloat(userResponses["ob_" + e.canonical_code]) || 0;
				return {
					amount: obAmount,
					debitCanonicalCode: e.canonical_code,
					debitDescription: e.display_name + " (Opening Balance)",
					debitGifi: e.gifi_code || null,
					creditCanonicalCode: CANONICAL.RETAINED_EARNINGS_OPENING,
					creditDescription: "Retained earnings/deficit – Start",
					creditGifi: GIFI.RETAINED_EARNINGS_OPENING,
					description: "Record opening balance for " + e.display_name + ": $" + obAmount.toLocaleString("en-CA"),
				};
			});
			return { ...resolved, status: "ready_to_propose", detail: null, prompt: null, inline_proposals: proposals };
		}
		let fields = debitLiabilities.map(function (e) {
			return { id: "ob_" + e.canonical_code, label: e.display_name + " — opening balance as of start of year", type: "number", placeholder: "0.00" };
		});
		return {
			...resolved, status: "waiting_for_human",
			detail: "Enter the opening balance for each liability account (from prior year financial statements or lender statements)",
			prompt: { question: "What was the balance at the start of the year?", input_fields: fields },
		};
	}

	if (response === "reclassify_liabilities") {
		let expenseMap = { debt: { code: CANONICAL.INTEREST_EXPENSE, name: "Interest and bank charges" }, tax_payable: { code: CANONICAL.PAYROLL_EXPENSE, name: "Salaries and wages" }, trade_payable: { code: CANONICAL.OPERATING_EXPENSE, name: "Other expenses" }, current_portion_ltd: { code: CANONICAL.INTEREST_EXPENSE, name: "Interest and bank charges" }, deferred_revenue: { code: CANONICAL.TRADE_REVENUE, name: "Trade sales of goods and services" } };
		let mappings = debitLiabilities.map(function (e) {
			let cls = e.semantic.role.class;
			let target = expenseMap[cls] || expenseMap.trade_payable;
			return { accountNumber: e.canonical_code, accountName: e.display_name, canonicalCode: target.code, canonicalName: target.name };
		});
		if (mappings.length > 0) {
			return { ...resolved, status: "ready_to_propose", detail: null, prompt: null, inline_mappings: mappings };
		}
		return { ...resolved, status: "completed", detail: "No liability accounts with debit balances found to reclassify", prompt: null };
	}

	if (response === "post_suspense") {
		let step1 = ctx.steps.find(function (s) { return s.id === "bs01_identify_difference"; });
		let bsDifference = step1 && step1.data ? step1.data.difference : 0;
		let liabTotal = 0;
		for (let e of debitLiabilities) liabTotal += Math.abs(e.net_balance);
		let suspenseAmount = bsDifference > 0 ? bsDifference + liabTotal : liabTotal;
		let shortSide = step1 && step1.data ? step1.data.shortSide : "Assets";
		let isAssetsShort = shortSide === "Assets";
		return {
			...resolved, status: "ready_to_propose", detail: null, prompt: null,
			inline_proposal: {
				amount: suspenseAmount,
				debitCanonicalCode: isAssetsShort ? CANONICAL.BS_DIFFERENCE : CANONICAL.RETAINED_EARNINGS_OPENING,
				debitDescription: isAssetsShort ? "Other current assets" : "Retained earnings/deficit – Start",
				debitGifi: isAssetsShort ? GIFI.SUSPENSE_BS_DIFFERENCE_ASSET : GIFI.RETAINED_EARNINGS_OPENING,
				creditCanonicalCode: isAssetsShort ? CANONICAL.RETAINED_EARNINGS_OPENING : CANONICAL.BS_DIFFERENCE,
				creditDescription: isAssetsShort ? "Retained earnings/deficit – Start" : "Other current liabilities",
				creditGifi: isAssetsShort ? GIFI.RETAINED_EARNINGS_OPENING : GIFI.SUSPENSE_BS_DIFFERENCE_LIABILITY,
				description: "Record $" + suspenseAmount.toLocaleString("en-CA") + " to suspense for investigation",
			},
		};
	}

	return resolved;
}

// ─── Registry ────────────────────────────────────────────────────────────────

export const equityCascades = {
	// EQ_01 — share capital existence (multi-phase, custom resolvers)
	"search_share_capital": { type: "custom", resolve: resolveSearchShareCapital },
	"resolve_share_capital": { type: "custom", resolve: resolveResolveShareCapital },
	"post_share_capital": { type: "custom", resolve: resolvePostShareCapital },

	// EQ_05 — retained earnings unmapped
	"eq05_determine_fix": {
		type: "labelResponse",
		labels: {
			re_unmapped: "Retained earnings exists but is unmapped",
			first_year: "First-year corporation",
		},
	},
	"eq05_resolve": {
		type: "branch", of: "eq05_determine_fix",
		when: {
			"first_year": { type: "acknowledge", detail: "Acknowledged — first-year corporation, RE will be established from net income" },
			"re_unmapped": {
				type: "pickAccount",
				keywords: ["retained", "earnings", "deficit", "accumulated", "surplus", "profit"],
				target: { canonicalCode: CANONICAL.RETAINED_EARNINGS, canonicalName: "Retained earnings/deficit – End" },
				question: "Which account should be mapped to Retained Earnings?",
			},
		},
	},

	// EQ_03 — share capital on a non-corp entity
	"eq03_determine_fix": {
		type: "labelResponse",
		labels: {
			change_to_corp: "Entity is actually a corporation",
			remap_to_partner: "Remap share capital to partner/owner capital",
		},
	},
	"eq03_resolve": {
		type: "branch", of: "eq03_determine_fix",
		when: {
			"change_to_corp": {
				type: "inlineContextChange",
				change: { field: "entity_type", csrsField: "entityType", currentValue: "sole_prop", newValue: "corporation", label: "Change entity type to Corporation" },
			},
			"remap_to_partner": { type: "custom", resolve: resolveEq03RemapToPartner },
		},
	},

	// EQ_02 — negative equity
	"eq02_determine_reason": {
		type: "labelResponse",
		labels: {
			accumulated_losses: "Accumulated losses over prior years",
			startup_phase: "Startup phase",
			turnaround_plan: "Management has a turnaround plan",
		},
	},
	"eq02_resolve": {
		type: "branch", of: "eq02_determine_reason",
		when: {
			"accumulated_losses": { type: "acknowledge", detail: "Acknowledged — accumulated losses. Consider adding a going concern note." },
			"startup_phase": { type: "acknowledge", detail: "Acknowledged — startup phase. Document management's expectations." },
			"turnaround_plan": { type: "acknowledge", detail: "Acknowledged — turnaround plan documented. Going concern note should be included." },
		},
	},

	// RE_01 — retained earnings continuity break
	"re01_determine_cause": {
		type: "labelResponse",
		labels: {
			opening_re_wrong: "Opening RE incorrect",
			dividends_not_recorded: "Dividends not recorded",
			prior_period_adj: "Prior-period adjustment",
		},
	},
	"re01_resolve": {
		type: "branch", of: "re01_determine_cause",
		when: {
			"opening_re_wrong,dividends_not_recorded,prior_period_adj": {
				type: "acknowledge", detail: "Acknowledged — escalate to engagement partner for correction",
			},
		},
	},

	// DIV_01 — dividends that erode equity
	"div01_determine_reason": {
		type: "labelResponse",
		labels: {
			surplus_covers: "Contributed surplus covers",
			board_approved: "Board approved",
			return_of_capital: "Return of capital",
		},
	},
	"div01_resolve": {
		type: "labelResponse",
		from: "div01_determine_reason",
		labels: {
			surplus_covers: "Acknowledged — surplus covers",
			board_approved: "Acknowledged — board approved",
			return_of_capital: "Acknowledged — return of capital",
		},
	},

	// BS_01 — balance sheet difference (custom: multiple resolution paths,
	// all use cross-check facts not peer steps)
	"bs01_resolve": { type: "custom", resolve: resolveBs01 },

	// EQ_04 — equity types on the wrong entity
	"eq04_determine_resolution": {
		type: "labelResponse",
		labels: {
			change_entity_type: "Resolution: Sole proprietorship",
			change_entity_partnership: "Resolution: Partnership",
			remap_accounts: "Resolution: Remap accounts to corporate equity types",
		},
	},
	"eq04_apply_fix": {
		type: "branch", of: "eq04_determine_resolution",
		when: {
			"change_entity_type": {
				type: "inlineContextChange",
				change: { field: "entity_type", csrsField: "entityType", currentValue: "corporation", newValue: "sole_prop", label: "Change entity type to Sole Proprietorship" },
			},
			"change_entity_partnership": {
				type: "inlineContextChange",
				change: { field: "entity_type", csrsField: "entityType", currentValue: "corporation", newValue: "partnership", label: "Change entity type to Partnership" },
			},
			"remap_accounts": { type: "custom", resolve: resolveEq04RemapAccounts },
		},
	},
};
